import { formatGr, normKey } from '@odhaczka/core';
import { useLiveQuery } from 'dexie-react-hooks';
import { Link } from 'react-router';
import { agoLabel, articleName } from '../../data/prices';
import { buildMergeMap, resolveWith } from '../../data/products';
import { db } from '../../db/db';
import type { ArticleRow, ProductRow } from '../../db/types';
import { cardCls, sectionTitleCls } from '../ui/classes';

const tokensOf = (q: string) => normKey(q).split(' ').filter(Boolean);
const hasAll = (key: string, tokens: string[]) => tokens.every((t) => key.split(' ').some((w) => w.startsWith(t)));

/** Wyniki wyszukiwania cen: produkty z list (z ceną ich artykułów) i towary z paragonów. */
export function PriceSearch({ query }: { query: string }) {
  const result = useLiveQuery(async () => {
    const tokens = tokensOf(query);
    if (tokens.length === 0) return null;
    const [products, articles] = await Promise.all([db.products.toArray(), db.articles.toArray()]);
    const resolve = resolveWith(buildMergeMap(products));
    const live = articles.filter((a) => !a.deleted);
    const latest = new Map<string, ArticleRow>();
    for (const a of live) {
      if (!a.productId) continue;
      const pid = resolve(a.productId);
      const prev = latest.get(pid);
      if (!prev || (prev.lastBoughtMs ?? 0) < (a.lastBoughtMs ?? 0)) latest.set(pid, a);
    }
    const prods: { product: ProductRow; article: ArticleRow }[] = products
      .filter((p) => !p.deleted && !p.mergedInto && latest.has(p.id) && hasAll(p.normKey, tokens))
      .map((p) => ({ product: p, article: latest.get(p.id)! }))
      .sort((a, b) => (b.article.lastBoughtMs ?? 0) - (a.article.lastBoughtMs ?? 0))
      .slice(0, 10);
    const arts = live
      .filter((a) => hasAll(normKey(`${articleName(a)} ${a.rawName ?? ''}`), tokens))
      .sort((a, b) => (b.lastBoughtMs ?? 0) - (a.lastBoughtMs ?? 0))
      .slice(0, 25);
    return { prods, arts };
  }, [query]);

  if (!result) return null;
  if (result.prods.length === 0 && result.arts.length === 0) return <p className="px-1 text-sm text-stone-500">Nic nie znalazłem w paragonach.</p>;
  return (
    <>
      {result.prods.length > 0 && (
        <section className="flex flex-col gap-2">
          <h2 className={sectionTitleCls}>Produkty z list</h2>
          <ul className={`${cardCls} divide-y divide-stone-200 dark:divide-stone-800`}>
            {result.prods.map(({ product, article }) => (
              <Row key={product.id} to={`/products/${encodeURIComponent(product.id)}`} title={product.name} sub={articleName(article)} article={article} />
            ))}
          </ul>
        </section>
      )}
      {result.arts.length > 0 && (
        <section className="flex flex-col gap-2">
          <h2 className={sectionTitleCls}>Towary z paragonów</h2>
          <ul className={`${cardCls} divide-y divide-stone-200 dark:divide-stone-800`}>
            {result.arts.map((a) => (
              <Row key={a.id} to={`/articles/${encodeURIComponent(a.id)}`} title={articleName(a)} sub={a.rawName !== articleName(a) ? (a.rawName ?? '') : ''} article={a} />
            ))}
          </ul>
        </section>
      )}
    </>
  );
}

function Row({ to, title, sub, article }: { to: string; title: string; sub: string; article: ArticleRow }) {
  return (
    <li>
      <Link to={to} className="flex items-center gap-3 px-4 py-3 active:bg-stone-50 dark:active:bg-stone-800">
        <div className="min-w-0 flex-1">
          <div className="truncate font-medium">{title}</div>
          <div className="truncate text-xs text-stone-500">
            {sub}
            {sub && article.lastBoughtMs ? ' · ' : ''}
            {article.lastBoughtMs ? agoLabel(article.lastBoughtMs) : ''}
          </div>
        </div>
        {article.lastShelfGr !== null && (
          <div className="shrink-0 font-semibold tabular-nums">
            {formatGr(article.lastShelfGr)}
            {article.weight ? '/kg' : ''}
          </div>
        )}
      </Link>
    </li>
  );
}
